import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { callGet, callPut } from '@/api/fetcher';
import { useIntegrations } from '@/hooks/useIntegrations';
import { selectAccountSlug } from '@/redux/session/selectors';
import { LiveChatBot, LiveChatBotProfile } from '@/shared/types/integration';

const toBotView = ({ groups, ...bot }: LiveChatBot) => ({
  ...bot,
  groupOptions: groups.map(({ id, name }) => ({ label: name, value: String(id) })),
});

export const useLiveChat = (deskId: string, integrationId: string) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const queryClient = useQueryClient();
  const { verifyIntegration } = useIntegrations(deskId, integrationId);
  const url = `/www/api/integrations/livechat/${slug}/desks/${deskId}/integrations/${integrationId}`;
  const botUrl = `${url}/bot`;

  const updateBot = (patch: Partial<LiveChatBot>) =>
    queryClient.setQueryData<LiveChatBot>([botUrl], (bot) => (bot ? { ...bot, ...patch } : bot));

  const { data, status: botStatus } = useQuery({
    queryKey: [botUrl],
    queryFn: (): Promise<LiveChatBot> => callGet(botUrl),
    select: toBotView,
  });

  const { mutate: saveGroups, status: saveGroupsStatus } = useMutation<void, Error, number[]>({
    mutationFn: (groupIds) => callPut(`${url}/groups`, { group_ids: groupIds }),
    onSuccess: (_, groupIds) => {
      updateBot({ group_ids: groupIds });
      toast.success(t('integrations.updated'));
      // The status depends on the groups, and LiveChat sends no event when they change.
      verifyIntegration();
    },
    onError: () => toast.error(t('integrations.livechat.group.save_error')),
  });

  const { mutate: saveProfile, status: saveProfileStatus } = useMutation<
    void,
    Error,
    LiveChatBotProfile
  >({
    mutationFn: (profile) => callPut(`${url}/profile`, profile),
    onSuccess: (_, profile) => {
      updateBot(profile);
      toast.success(t('integrations.updated'));
    },
    onError: () => toast.error(t('integrations.livechat.profile.save_error')),
  });

  return {
    name: data?.name,
    avatar: data?.avatar,
    groupOptions: data?.groupOptions ?? [],
    groupIds: data?.group_ids ?? [],
    botStatus,
    saveGroups,
    saveGroupsStatus,
    saveProfile,
    saveProfileStatus,
  };
};
